import { useMutation, useQuery } from '@apollo/client/react';
import { groupRequestTypes } from '@src/constants/group-request-types';
import { ScoSignupRequest } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
const SIGNUP_REQUEST_FRAGMENT = gql`
  fragment _ on ScoSignupRequest {
    id
    bodyAsMarkdown
    companyElevatorPitch
    fundingGoalInDollars
    groupRequestId
    groupRequestTypeKey
    investorIndustryTagTitles
    pitchDeckUrl
    recommendationAsMarkdown
    recommendedFeaturesAsJson
    recommendedProductId
    signupRequestId
    skillTagTitles
    locationCity
    locationState
    createdByIpLocation
    tags {
      id
      tagId
      tagTypeKey
      title
    }
  }
`;
export const useSignupRequestQuery = (options = {}) => {
  return useQuery(gql`
      query ($signupRequestId: String) {
        scoSignupRequest(signupRequestId: $signupRequestId) {
          ${SIGNUP_REQUEST_FRAGMENT}
        }
      }
    `, {
    ...options
  });
};
export const useUpsertSignupRequestMutation = () => {
  return useMutation(gql`
    mutation ($input: UpsertSignupRequestInput!) {
      upsertSignupRequest(input: $input) {
        signupRequest {
          ${SIGNUP_REQUEST_FRAGMENT}
        }
      }
    }
  `);
};
export const generateSignupRequestPreviewTitle = (formValues: Partial<ScoSignupRequest> = {}) => {
  if (!formValues?.groupRequestTypeKey) {
    return '';
  }
  const requestType = groupRequestTypes.find(t => t.key === formValues.groupRequestTypeKey);
  const typeLabel = requestType?.label || requestType?.title || formValues.groupRequestTypeKey;
  const parts = [];
  parts.push('A');
  if (formValues.investorIndustryTagTitles?.length) {
    parts.push(`**${formValues.investorIndustryTagTitles.join('/')}**`);
  }
  parts.push('startup');
  switch (formValues.groupRequestTypeKey) {
    case 'funding':
      if (formValues.fundingGoalInDollars) {
        const formattedAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(formValues.fundingGoalInDollars);
        parts.push(`looking to **Raise ${formattedAmount}**`);
      } else {
        parts.push('looking to **Raise Funding**');
      }
      break;
    case 'cofounder':
      parts.push('looking for a **Co-Founder**');
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('who knows about');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
    case 'advisor':
      parts.push('looking for an **Advisor**');
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('who can help with');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
    case 'talent':
      parts.push('looking to **Hire Talent**');
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('who can work on');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
    case 'get-customers':
      parts.push('looking to **Get Customers**');
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('and help with');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
    case 'goal':
      parts.push('looking to **Accomplish a Goal**');
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('focused on');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
    default:
      parts.push(`looking to **${typeLabel.toLowerCase()}**`);
      if (formValues.skillTagTitles?.length > 0) {
        parts.push('related to');
        parts.push(`**${formValues.skillTagTitles.join(', ')}**`);
      }
      break;
  }
  let firstSentence = parts.join(' ');
  if (firstSentence) {
    firstSentence += '.';
  }
  return firstSentence;
};